<template>
  <div class="flex-lg-1 h-screen overflow-y-lg-auto">
    <!-- Header -->
    <header class="bg-surface-primary border-bottom pt-6">
      <div class="container-fluid">
        <div class="row align-items-center">
          <div class="col-md-7 col-12 mb-4">
            <h1 class="h2 ls-tight mb-1">
              <i class="bi bi-hdd-network me-2"></i>{{ pageTitle }} Management
            </h1>
            <p class="text-sm text-muted mb-0">
              Upload, register, and manage your {{ pageTitle.toLowerCase() }} resources for downstream calculations.
            </p>
          </div>

          <div class="col-md-5 col-12 text-md-end mb-4">
            <div class="d-flex gap-2 justify-content-md-end">
              <a
                :href="exampleLink"
                target="_blank"
                class="btn btn-sm btn-outline-primary rounded-pill px-4 shadow-none"
              >
                <i class="bi bi-file-earmark-arrow-down me-2"></i>Download Template
              </a>
              <button class="btn btn-sm btn-outline-secondary rounded-pill px-4 shadow-none" @click="fetchFiles">
                <i class="bi bi-arrow-clockwise me-2"></i>Refresh
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>

    <main class="py-6 bg-surface-secondary">
      <div class="container-fluid">
        <!-- Step Status -->
        <!-- Steps (Same as GWAS, wording adjusted for LD) -->
<div class="card shadow-sm border-0 rounded-4 mb-8">
  <div class="card-body p-6">
    <h6 class="text-uppercase text-muted ls-wide fw-bold text-xs mb-4">Current Status</h6>

    <div class="row g-0 text-center">
      <div class="col-md-4">
        <div class="d-flex flex-column align-items-center px-4">
          <div :class="stepStatus.metadata ? 'bg-success' : 'bg-primary'" class="icon-shape text-white rounded-circle mb-3 shadow">
            <i v-if="stepStatus.metadata" class="bi bi-check-lg"></i>
            <span v-else>1</span>
          </div>
          <h6 class="fw-bold mb-1">Define Metadata</h6>
          <p class="text-xs text-muted mb-0">Set display name, ancestry, and description.</p>
        </div>
      </div>

      <div class="col-md-4 border-start-md">
        <div class="d-flex flex-column align-items-center px-4">
          <div
            :class="uploadSuccess ? 'bg-success' : (isUploading ? 'bg-primary-animated' : (stepStatus.metadata ? 'bg-primary' : 'bg-light text-muted'))"
            class="icon-shape text-white rounded-circle mb-3 shadow"
          >
            <i v-if="uploadSuccess" class="bi bi-check-lg"></i>
            <div v-else-if="isUploading" class="spinner-border spinner-border-sm" role="status"></div>
            <span v-else>2</span>
          </div>
          <h6 class="fw-bold mb-1">Stream Upload</h6>
          <p class="text-xs text-muted mb-0">Upload <code>.gz</code> to PB storage.</p>
        </div>
      </div>

      <div class="col-md-4 border-start-md">
        <div class="d-flex flex-column align-items-center px-4">
          <div :class="isRegistered ? 'bg-success text-white' : 'bg-light text-muted'" class="icon-shape rounded-circle mb-3 shadow">
            <i v-if="isRegistered" class="bi bi-check-all"></i>
            <span v-else>3</span>
          </div>
          <h6 class="fw-bold mb-1">Registered</h6>
          <p class="text-xs text-muted mb-0">Indexed and visible in library.</p>
        </div>
      </div>
    </div>

    <!-- Guidance -->
    <div class="mt-5 alert border-0 mb-0 d-flex align-items-start bg-soft-primary text-primary rounded-4">
      <i class="bi bi-info-circle-fill me-3 fs-5 mt-1"></i>
      <div class="text-xs">
        <div class="fw-bold mb-1">Guidance</div>
        <ul class="mb-0 ps-3">
          <li>Use a unique Display Name (preferably include panel + population + year/version).</li>
          <li>Select the correct <b>Ancestry</b> to match downstream pipeline settings.</li>
          <li>Only <code>.gz</code> is accepted. After upload, the system registers the resource and maps it to a storage key.</li>
        </ul>
      </div>
    </div>
  </div>
</div>

        <!-- Upload Card -->
        <div class="card mb-6 shadow-sm border-0">
          <div class="card-body">
            <div class="d-flex align-items-start justify-content-between gap-3 mb-3">
              <div>
                <h5 class="mb-1 text-heading">Upload New Resource</h5>
                <p class="text-sm text-muted mb-0">{{ uploadHint }}</p>
              </div>
              <span class="badge rounded-pill bg-light text-muted px-3 py-2">
                Category: {{ (type || 'unknown').toUpperCase() }}
              </span>
            </div>

            <!-- Metadata form -->
            <div class="row g-3 mb-4">
              <div class="col-md-4">
                <label class="form-label fw-semibold text-sm">
                  Display Name
                  <el-tooltip content="A unique display name for this resource." placement="top">
                    <i class="bi bi-question-circle text-muted ms-1"></i>
                  </el-tooltip>
                </label>
                <input
                  type="text"
                  class="form-control form-control-sm shadow-none"
                  v-model.trim="fileName"
                  @blur="checkName"
                  :disabled="isUploading"
                  placeholder="e.g. Atlas_EUR_2026"
                />
                <div class="form-text text-xs text-muted">
                  Keep it short. Use project/population/time if possible.
                </div>
              </div>

              <div class="col-md-4">
                <label class="form-label fw-semibold text-sm">
                  Ancestry (Population)
                  <el-tooltip content="Used to determine the appropriate reference population." placement="top">
                    <i class="bi bi-question-circle text-muted ms-1"></i>
                  </el-tooltip>
                </label>
                <select class="form-select form-select-sm shadow-none" v-model="pop" :disabled="isUploading">
                  <option :value="-1">Please select...</option>
                  <option v-for="item in popList" :key="item" :value="item">{{ item.toUpperCase() }}</option>
                </select>
                <div class="form-text text-xs text-muted">
                  Choose the population that matches your file.
                </div>
              </div>

              <div class="col-md-4">
                <label class="form-label fw-semibold text-sm">Description (Optional)</label>
                <input
                  type="text"
                  class="form-control form-control-sm shadow-none"
                  v-model.trim="description"
                  :disabled="isUploading"
                  placeholder="Brief notes, version, source..."
                />
                <div class="form-text text-xs text-muted">
                  Example: “LD reference built from 1KG phase 3”.
                </div>
              </div>
            </div>

            <!-- Validation / Tips -->
            <div class="alert alert-info border-0 mb-4 d-flex align-items-start bg-light-soft text-muted">
              <i class="bi bi-lightbulb-fill me-3 fs-5 mt-1"></i>
              <div class="text-xs">
                <div class="fw-bold text-heading mb-1">Upload checklist</div>
                <ul class="mb-0 ps-3">
                  <li>File must be compressed: <code>.gz</code>.</li>
                  <li>Make sure the selected Ancestry matches your data source.</li>
                  <li>For naming: avoid spaces; use underscores.</li>
                </ul>
              </div>
            </div>

            <!-- Uploader -->
            <div class="p-4 border border-2 border-dashed rounded-3 bg-light-soft" :class="{ 'opacity-60': !stepStatus.metadata }">
              <div v-if="!stepStatus.metadata" class="text-center py-4">
                <p class="text-sm text-muted mb-0">
                  <i class="bi bi-lock-fill me-2"></i>Complete metadata to unlock uploader
                </p>
              </div>

              <div v-show="stepStatus.metadata" class="uploader-area">
                <div class="d-flex flex-column align-items-center justify-content-center py-4">
                  <!-- Select file -->
                  <label class="btn btn-sm btn-primary px-4 mb-2" v-if="!uploading">
                    <i class="bi bi-cloud-arrow-up me-2"></i>Select .gz File
                    <input
                      type="file"
                      class="visually-hidden"
                      accept=".gz"
                      ref="fileInput"
                      @change="onPickFile"
                      :disabled="uploading"
                    />
                  </label>

                  <p v-if="!uploading" class="text-xs text-muted mb-0">
                    Supported: <code>.gz</code> · Max recommended: {{ prettyBytes(maxFileBytes) }}
                  </p>

                  <!-- Progress -->
                  <div v-else class="w-100" style="max-width: 520px;">
                    <div class="d-flex justify-content-between mb-1">
                      <span class="text-xs fw-bold" :class="statusText === 'failed' ? 'text-danger' : 'text-primary'">
                        {{ statusText.toUpperCase() }}
                      </span>
                      <span class="text-xs fw-bold text-primary">{{ progress }}%</span>
                    </div>

                    <div class="progress mb-2" style="height: 6px;">
                      <div class="progress-bar progress-bar-animated" :class="colorClass" :style="`width:${progress}%`"></div>
                    </div>

                    <div class="d-flex align-items-center justify-content-between">
                      <span class="text-xs text-muted">{{ fileMeta.name }}</span>
                      <div class="d-flex gap-2">
                        <button class="btn btn-sm btn-outline-secondary" @click="cancelUpload" type="button">
                          <i class="bi bi-x-circle me-1"></i>Cancel
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Success summary -->
                  <div v-if="uploadSuccess" class="mt-4 w-100" style="max-width: 720px;">
                    <div class="alert border-0 bg-soft-success text-success d-flex align-items-start mb-0">
                      <i class="bi bi-check-circle-fill me-3 fs-5 mt-1"></i>
                      <div class="text-xs">
                        <div class="fw-bold mb-1">Upload completed</div>
                        <div class="text-muted">
                          <div><span class="fw-semibold">Name:</span> {{ fileName }}</div>
                          <div><span class="fw-semibold">Ancestry:</span> {{ String(pop).toUpperCase() }}</div>
                          <div><span class="fw-semibold">File:</span> {{ fileMeta.name }} ({{ prettyBytes(fileMeta.size) }})</div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

          </div>
        </div>

        <!-- Library Card -->
        <div class="card shadow-sm border-0">
          <div class="card-header bg-white border-bottom py-4">
            <div class="d-flex align-items-center gap-3">
              <div>
                <h5 class="mb-1">Resource Library</h5>
                <p class="text-sm text-muted mb-0">Browse uploaded resources in this category.</p>
              </div>
              <div class="ms-auto d-flex align-items-center gap-2">
                <span class="badge bg-soft-primary text-primary rounded-pill px-3">{{ total }} Records</span>
              </div>
            </div>

            <!-- Search & filter -->
            <div class="row g-2 mt-3">
              <div class="col-md-5">
                <div class="input-group input-group-sm">
                  <span class="input-group-text bg-white"><i class="bi bi-search"></i></span>
                  <input
                    type="text"
                    class="form-control shadow-none"
                    v-model.trim="keyword"
                    placeholder="Search by display name..."
                    @keyup.enter="fetchFiles"
                  />
                </div>
              </div>
              <div class="col-md-3">
                <select class="form-select form-select-sm shadow-none" v-model="statusFilter" @change="fetchFiles">
                  <option value="">All Status</option>
                  <option value="AVAILABLE">AVAILABLE</option>
                  <option value="UPLOADING">UPLOADING</option>
                  <option value="FAILED">FAILED</option>
                </select>
              </div>
              <div class="col-md-4 d-flex justify-content-md-end gap-2">
                <button class="btn btn-sm btn-outline-secondary" @click="resetFilters">
                  <i class="bi bi-eraser me-1"></i>Reset
                </button>
                <button class="btn btn-sm btn-outline-primary" @click="fetchFiles">
                  <i class="bi bi-arrow-repeat me-1"></i>Apply
                </button>
              </div>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-hover table-nowrap align-middle border-light">
              <thead class="bg-light-soft">
                <tr>
                  <th class="text-start ps-6 py-4 text-xs fw-bold text-uppercase text-muted">Resource & Key</th>
                  <th class="text-start py-4 text-xs fw-bold text-uppercase text-muted">Ancestry</th>
                  <th class="text-start py-4 text-xs fw-bold text-uppercase text-muted">Upload Info</th>
                  <th class="text-start py-4 text-xs fw-bold text-uppercase text-muted">Status</th>
                  <th class="text-start py-4 text-xs fw-bold text-uppercase text-muted">Retention</th>
                  <th class="text-end pe-6 py-4 text-xs fw-bold text-uppercase text-muted">Operation</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="file in files.fileList" :key="file.file_id" class="table-row-hover">
                  <td class="ps-8">
                    <div class="d-flex align-items-center">
                      <div class="avatar avatar-sm bg-soft-primary text-primary rounded me-3">
                        <i class="bi bi-file-earmark-binary"></i>
                      </div>
                      <div class="min-w-0">
                        <span class="text-heading fw-bold d-block text-truncate">{{ file.display_name }}</span>
                        <code class="x-small text-muted font-monospace d-inline-block text-truncate" style="font-size: 0.7rem; max-width: 520px;">
                          {{ file.storage_key || file.identifier }}
                        </code>
                      </div>
                    </div>
                  </td>

                  <td>
                    <span class="badge rounded-pill bg-soft-info text-info px-3">
                      {{ getAncestry(file) }}
                    </span>
                  </td>

                  <td>
                    <div class="d-flex flex-column">
                      <span class="small text-dark">{{ file.create_time }}</span>
                      <span class="x-small text-muted">ID: {{ file.identifier }}</span>
                    </div>
                  </td>

                  <td>
                    <span class="d-inline-flex align-items-center">
                      <span class="badge-dot-lg me-2" :class="isOkStatus(file.status) ? 'bg-success' : 'bg-warning'"></span>
                      <span class="text-sm fw-semibold text-uppercase"
                        :class="isOkStatus(file.status) ? 'text-success' : 'text-warning'">
                        {{ file.status }}
                      </span>
                    </span>
                  </td>

                  <td>
                    <div class="d-flex align-items-center">
                      <i class="bi bi-clock-history me-2 text-danger opacity-70"></i>
                      <span class="small text-danger fw-medium">{{ file.expire_time || "N/A" }}</span>
                    </div>
                  </td>

                  <td class="text-end pe-6">
                    <button class="btn btn-sm btn-square btn-neutral text-danger" @click="deleteFile(file.file_id)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>

                <tr v-if="files.fileList.length === 0">
                  <td colspan="6" class="text-center py-20">
                    <img src="https://illustrations.popsy.co/gray/uploading-files.svg" style="height: 120px;" class="mb-4 opacity-50" />
                    <p class="text-muted text-sm mb-1">No resources found.</p>
                    <p class="text-xs text-muted mb-0">Try adjusting filters or upload a new file.</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="card-footer border-0 py-5">
            <el-pagination
              @current-change="changePage"
              :current-page="currentPage"
              :background="true"
              layout="prev, pager, next"
              :total="total"
              :page-size="pageSize"
              class="d-flex justify-content-center"
            />
          </div>
        </div>

      </div>
    </main>
  </div>
</template>

<script>
import { listFiles, registerFile, deleteFileResource, uploadFileRecord } from "@/api/storage";
import { isEmpty } from "@/utils/validate";
import { Message, MessageBox } from "element-ui";
import { pb } from "@/utils/pocketbase";

export default {
  name: "ReferenceItem",
  props: {
    pageTitle: { type: String, default: "Reference" },
    type: { type: String, required: true },
  },
  data() {
    return {
      exampleLink: "https://github.com/xinyu-c9/PGS-hub/raw/main/LDreference_example/LD_reference_example.gz",

      files: { fileList: [] },
      total: 0,
      pageSize: 5,
      currentPage: 1,

      // form
      description: "",
      fileName: "",
      popList: ["eur", "afr", "sas", "eas"],
      pop: -1,

      // library filter
      keyword: "",
      statusFilter: "",

      // upload state
      isUploading: false,
      uploadSuccess: false,
      isRegistered: false,
      progress: 0,
      colorClass: "bg-primary",
      statusText: "",
      uploading: false,
      fileMeta: { name: "", size: 0 },

      // controls
      maxFileBytes: 2 * 1024 * 1024 * 1024, // 2GB recommended
      uploadController: null,
    };
  },
  computed: {
    stepStatus() {
      const hasMetadata = !isEmpty(this.fileName) && this.pop !== -1;
      return {
        metadata: hasMetadata,
        upload: this.isUploading || this.uploadSuccess,
        index: this.isRegistered,
      };
    },
    uploadHint() {
      const t = (this.type || "").toLowerCase();
      if (t.includes("ld")) return "Upload LD reference files used for LD-aware calculations.";
      if (t.includes("gwas")) return "Upload GWAS summary statistics to support scoring and reporting.";
      if (t.includes("ref")) return "Upload reference resources used by downstream analysis modules.";
      return "Upload a compressed resource file for processing and registration.";
    },
  },
  methods: {
    // --- Display helpers ---
    getAncestry(file) {
      // ✅ 期望后端直接返回 ancestry（最正确）
      if (file?.ancestry) return String(file.ancestry).toUpperCase();

      // ✅ 兜底：如果后端没返回（或你历史数据在 tags 里）
      const tags = file?.tags;
      if (!tags) return "N/A";

      try {
        const obj = typeof tags === "string" ? JSON.parse(tags) : tags;
        return String(obj?.ancestry || obj?.pop || "N/A").toUpperCase();
      } catch {
        return "N/A";
      }
    },

    formatDateTime(dateStr) {
      if (!dateStr) return "-";
      // dateStr 可能是 locale string，也可能是 ISO
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return String(dateStr);
      return d.toLocaleString();
    },

    prettyBytes(bytes) {
      if (!bytes && bytes !== 0) return "-";
      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB", "TB"];
      let i = 0;
      let v = bytes;
      while (v >= k && i < sizes.length - 1) {
        v /= k;
        i++;
      }
      return `${v.toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
    },

    isOkStatus(status) {
      const s = (status || "").toLowerCase();
      return s === "finished" || s === "available" || s === "success";
    },

    resetFilters() {
      this.keyword = "";
      this.statusFilter = "";
      this.currentPage = 1;
      this.fetchFiles();
    },

    // --- Upload controls ---
    safeResetInput() {
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },

    cancelUpload() {
      try {
        if (this.uploadController) this.uploadController.abort();
      } catch {}
      this.uploading = false;
      this.isUploading = false;
      this.colorClass = "bg-danger";
      this.statusText = "cancelled";
      Message.warning("Upload cancelled.");
      this.safeResetInput();
    },

    validateBeforeUpload(file) {
      if (!pb.authStore?.isValid) {
        Message.error("Session expired. Please login again.");
        return false;
      }
      if (isEmpty(this.fileName)) {
        Message.error("Please enter Display Name first.");
        return false;
      }
      if (this.pop === -1) {
        Message.error("Please select Ancestry first.");
        return false;
      }
      if (!file) {
        Message.error("No file selected.");
        return false;
      }
      if (!String(file.name || "").toLowerCase().endsWith(".gz")) {
        Message.error("Only .gz files are supported.");
        return false;
      }
      if (file.size > this.maxFileBytes) {
        Message.warning(`File is very large (${this.prettyBytes(file.size)}). Upload may be slow or fail depending on gateway limits.`);
        // 不硬拦截，只提醒
      }
      return true;
    },

    async onPickFile(e) {
      const file = e.target.files?.[0];
      if (!this.validateBeforeUpload(file)) {
        this.safeResetInput();
        return;
      }

      this.fileMeta = { name: file.name, size: file.size };
      this.uploading = true;
      this.isUploading = true;
      this.uploadSuccess = false;
      this.isRegistered = false;

      this.progress = 0;
      this.statusText = "uploading";
      this.colorClass = "bg-primary";

      this.uploadController = new AbortController();

      try {
        const formData = new FormData();

        let kindVal = "other";
        const typeLower = (this.type || "").toLowerCase();
        if (typeLower.includes("ref")) kindVal = "ref";
        else if (typeLower.includes("gwas")) kindVal = "gwas";
        else if (typeLower.includes("ld")) kindVal = "ld";

        formData.append("file", file);
        formData.append("owner", pb.authStore.model.id);
        formData.append("kind", kindVal);
        formData.append("scope", "private");
        formData.append("name", this.fileName);
        formData.append("bucket", "pocketbase");
        formData.append("key", "pending");
        formData.append("size_bytes", String(file.size));
        formData.append("status", "uploading");

        // ✅ 上传记录里也写 ancestry（但记住：列表显示来自 listFiles 返回）
        formData.append("ancestry", this.pop);

        // tags: 扩展信息（给历史数据兜底用）
        const tags = {
          pop: this.pop,
          ancestry: this.pop,
          displayName: this.fileName,
          description: this.description,
          original_filename: file.name,
          uploaded_at: new Date().toISOString(),
        };
        formData.append("tags", JSON.stringify(tags));

        const record = await uploadFileRecord(
          formData,
          (evt) => {
            if (typeof evt === "number") this.progress = Math.floor(evt);
            else if (evt?.total) this.progress = Math.floor((evt.loaded / evt.total) * 100);
          },
          this.uploadController.signal
        );

        this.progress = 100;
        this.statusText = "successful";
        this.colorClass = "bg-success";
        this.isUploading = false;
        this.uploadSuccess = true;

        await this.registerAfterUpload({
          filePath: record.id,
          fileName: this.fileName,
          pop: this.pop,
        });

        setTimeout(() => {
          this.uploading = false;
        }, 400);

      } catch (err) {
        this.uploading = false;
        this.isUploading = false;
        this.uploadSuccess = false;
        this.colorClass = "bg-danger";
        this.statusText = err?.name === "AbortError" ? "cancelled" : "failed";
        Message.error(err?.message || "Upload failed");
      } finally {
        this.safeResetInput();
      }
    },

    async registerAfterUpload(data) {
      // ✅ 这里建议同时传 ancestry & pop，兼容后端参数命名
      const payload = {
        storage_path: data.filePath,
        display_name: this.fileName || data.fileName,
        description: this.description,
        category: this.type,

        ancestry: this.pop,
        pop: this.pop,
      };

      const res = await registerFile(payload);
      if (res.code === 0) {
        this.isRegistered = true;
        Message.success("File registered successfully!");
        setTimeout(() => {
          this.resetInputs();
          this.fetchFiles();
        }, 300);
      } else {
        Message.error(res.message || "Registration failed");
        throw new Error(res.message || "Registration failed");
      }
    },

    async fetchFiles() {
      const res = await listFiles({
        category: this.type,
        page_size: this.pageSize,
        page_number: this.currentPage,
        // 如果你愿意改后端 listFiles 支持 keyword/statusFilter，可把这俩传过去：
        // keyword: this.keyword,
        // status: this.statusFilter,
      });

      if (res.code === 0) {
        // ✅ 前端可筛一次（后端不支持过滤时）
        let list = res.data.file_list || [];
        const kw = (this.keyword || "").trim().toLowerCase();
        if (kw) {
          list = list.filter((x) => String(x.display_name || "").toLowerCase().includes(kw));
        }
        if (this.statusFilter) {
          list = list.filter((x) => String(x.status || "").toUpperCase() === this.statusFilter);
        }

        this.files.fileList = list;
        this.total = res.data.total_count || 0;
      } else {
        Message.error(res.message || "Failed to load files");
      }
    },

    resetInputs() {
      this.fileName = "";
      this.description = "";
      this.pop = -1;

      this.isUploading = false;
      this.uploadSuccess = false;
      this.isRegistered = false;

      this.progress = 0;
      this.colorClass = "bg-primary";
      this.statusText = "";
      this.uploading = false;
      this.fileMeta = { name: "", size: 0 };
    },

    async deleteFile(id) {
      try {
        await MessageBox.confirm("Permanently delete this file?", "Warning", { type: "warning" });
        const res = await deleteFileResource(id);
        if (res.code === 0) {
          Message.success("File removed");
          this.fetchFiles();
        } else {
          Message.error(res.message || "Delete failed");
        }
      } catch (e) {}
    },

    checkName() {
      if (isEmpty(this.fileName)) return;
      // ⚠️ 这里只检查当前页，严格唯一性应由后端保证
      if (this.files.fileList.some((f) => f.display_name === this.fileName)) {
        Message.warning("Duplicate name detected on current page. Please choose another.");
      }
    },

    changePage(val) {
      this.currentPage = val;
      this.fetchFiles();
    },
  },
  mounted() {
    this.fetchFiles();
  },
};
</script>

<style scoped>
/* 基础组件 */
.icon-sm { width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 0.85rem; }
.icon-box-sm { width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; font-size: 1.1rem; }

.bg-light-soft { background-color: #f8f9fa; }
.bg-soft-primary { background-color: rgba(92, 107, 255, 0.10) !important; }
.bg-soft-info { background-color: rgba(13, 202, 240, 0.10) !important; }
.bg-soft-success { background-color: rgba(25, 135, 84, 0.12) !important; }

.border-dashed { border-style: dashed !important; }

/* 状态圆点 */
.badge-dot-lg {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px rgba(0,0,0,0.05);
}

/* 表格 */
.table { border-collapse: separate; border-spacing: 0; }
.table thead th { border-bottom: 1px solid #edf2f7 !important; }
.table-row-hover:hover { background-color: #fcfdfe !important; transition: background 0.2s; }

td, th { text-align: left !important; vertical-align: middle; }

/* 文本截断辅助 */
.min-w-0 { min-width: 0; }
.text-truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* uploader */
.uploader-area { width: 100%; }
</style>